import React, { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Alert } from 'reactstrap';
import PropTypes from 'prop-types';
import identityClient from '../../../clients/identityClient';
import BlockingSpinnerOverlay from 'components/BlockingSpinnerOverlay';
import EmailPhoneContactSelect from 'components/EmailPhoneContactSelect';
import { registrationSteps } from '../PreSignUp';
import { WORKFLOW_TYPE_LINK_ACCOUNT } from 'helpers/registrationWorkflow';

const SelectPrimaryLogin = ({ navigate }) => {
  const location = useLocation();
  const history = useHistory();
  const {
    emails = [],
    phoneNumbers = [],
    verifyInfo,
    hStreamId: directHStreamId,
    defaultRank1Email = '',
    registrationData = null,
    registrationEmail = '',
    newEmail = '',
    email: stateEmail = '',
    isAccountMatching = false,
    password: statePassword = '',
  } = location.state || {};

  const hStreamId = verifyInfo?.hstreamId || directHStreamId;
  const registrationPassword =
    registrationData?.password || statePassword || '';
  const workflowTypeValue =
    location.state?.workflowType || 'NewRegistration';
  const isProvisioned = location.state?.isProvisioned === true;
  const isConfirmed = location.state?.isConfirmed === true;
  const isLinkAccount = workflowTypeValue === WORKFLOW_TYPE_LINK_ACCOUNT;

  const defaultSelectedEmail = (() => {
    if (emails.length === 0) return '';
    return (
      defaultRank1Email ||
      emails.find((e) => e.rank === 1)?.value ||
      emails[0]?.value ||
      ''
    );
  })();

  const defaultSelectedPhone =
    phoneNumbers.length === 0
      ? ''
      : phoneNumbers.find((p) => p.rank === 1)?.value ||
        (phoneNumbers.length === 1 ? phoneNumbers[0].value : undefined) ||
        phoneNumbers[0]?.value ||
        '';

  const [selectedEmail, setSelectedEmail] = useState(defaultSelectedEmail);
  const [selectedPhone, setSelectedPhone] = useState(defaultSelectedPhone);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCancel = () => {
    const wf = location.state?.workflowType || 'NewRegistration';
    const reg = registrationData || {};
    const email = reg.email || location.state?.email || '';
    const firstName = reg.firstName || location.state?.firstName || '';
    const lastName = reg.lastName || location.state?.lastName || '';
    const existingPhone = reg.phone || location.state?.phone || '';
    const phone = isAccountMatching ? '' : existingPhone;
    const existingHstreamId =
      verifyInfo?.hstreamId ||
      directHStreamId ||
      location.state?.hStreamId ||
      location.state?.hstreamId ||
      '';
    const hStreamIdForReset = isAccountMatching ? '' : existingHstreamId;

    const resetState = {
      workflowType: wf,
      email,
      firstName,
      lastName,
      phone,
      password: '',
      hStreamId: hStreamIdForReset,
      hstreamId: hStreamIdForReset,
      isMobileRequired: location.state?.isMobileRequired,
      isConfirmed: isAccountMatching ? false : location.state?.isConfirmed,
      registrationData: {
        ...reg,
        email,
        firstName,
        lastName,
        phone,
        password: '',
      },
      emails: [],
      phoneNumbers: [],
      verifyInfo: undefined,
      isAccountMatching: false,
      isRegistrationFlow: false,
      defaultRank1Email: '',
      newEmail: '',
      registrationEmail: '',
    };

    if (navigate) {
      navigate(registrationSteps.REGISTRATION, resetState);
    } else {
      history.push({
        pathname:
          wf === WORKFLOW_TYPE_LINK_ACCOUNT
            ? '/account/link/registration'
            : '/account/registration',
        search: window.location.search,
        state: resetState,
      });
    }
  };

  const handleSubmit = async () => {
    if (emails.length > 0 && !selectedEmail) {
      setError('Please select an email.');
      return;
    }
    if (phoneNumbers.length > 0 && !selectedPhone) {
      setError('Please select a mobile number.');
      return;
    }
    if (emails.length > 0 && !emails.find((e) => e.value === selectedEmail)) {
      setError('Please select a verified email from the list.');
      return;
    }
    if (
      phoneNumbers.length > 0 &&
      !phoneNumbers.find((p) => p.value === selectedPhone)
    ) {
      setError('Please select a verified phone number from the list.');
      return;
    }
    if (!hStreamId) {
      setError('Account information is missing. Please restart registration.');
      return;
    }
    if (!registrationPassword) {
      setError(
        'Your registration password is missing. Please go back and complete registration.',
      );
      return;
    }

    setIsLoading(true);
    setError(null);

    const registrationEmailForApi =
      registrationEmail ||
      newEmail ||
      stateEmail ||
      registrationData?.email ||
      '';

    try {
      await identityClient.setAdminPasswordAndPrimaryLogin({
        hStreamId,
        workflowType: workflowTypeValue,
        isProvisioned,
        isConfirmed,
        password: registrationPassword,
        selectedPrimaryEmail: emails.length > 0 ? selectedEmail : '',
        selectedPrimaryPhone: phoneNumbers.length > 0 ? selectedPhone : '',
        ...(registrationEmailForApi
          ? { registrationEmail: registrationEmailForApi }
          : {}),
      });

      const params = new URLSearchParams(window.location.search);
      const queryString = params.toString();

      let successState = {
        isFromRegistration: true,
        accountMatched: isAccountMatching,
        selectedPrimaryEmail: selectedEmail || '',
        selectedPrimaryPhone: selectedPhone || '',
      };

      if (isLinkAccount) {
        const linkUserResponse = await identityClient.linkUser(
          hStreamId,
          registrationEmailForApi,
          queryString,
        );
        successState = {
          ...successState,
          code: linkUserResponse?.code,
        };
      }

      if (navigate) {
        navigate(registrationSteps.SUCCESS, successState);
      } else {
        history.push({
          pathname: isLinkAccount ? '/account/link/success' : '/account/success',
          state: successState,
        });
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          'An error occurred while setting your primary login. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const topAlert = isAccountMatching ? (
    <Alert color="warning" className="mb-3">
      This mobile number is already linked to an hStream account. The email you
      provided will be added to that account. Choose which email and phone to use
      for login. The password you created during registration will apply to this account.
    </Alert>
  ) : null;

  const headerContent = (
    <p
      className="mb-3"
      style={{ fontSize: '14px', lineHeight: '1.5' }}
    >
      <strong>
        Please confirm which email and phone you would like to use to log into
        your hStream applications.
      </strong>
    </p>
  );

  return (
    <div
      className="position-relative"
      aria-busy={isLoading || undefined}
    >
      <BlockingSpinnerOverlay show={isLoading} ariaLabel="Loading..." />
      <EmailPhoneContactSelect
        selectionMode="perChannel"
        emails={emails}
        phoneNumbers={phoneNumbers}
        headerContent={headerContent}
        emailSectionLabel="Login with your email"
        phoneSectionLabel="Login with your mobile number"
        maskEmail={false}
        maskPhone={false}
        selectedEmail={selectedEmail}
        selectedPhone={selectedPhone}
        onEmailChange={setSelectedEmail}
        onPhoneChange={setSelectedPhone}
        topAlert={topAlert}
        errorAlert={error || undefined}
        emptyContactsMessage="No verified contacts found. Please contact support."
        primaryButtonId="selectPrimaryLoginContinue"
        onPrimaryClick={handleSubmit}
        isLoading={isLoading}
        primaryDisabled={emails.length === 0 && phoneNumbers.length === 0}
        showCancel
        onCancel={handleCancel}
        cancelButtonId="selectPrimaryLoginCancel"
        buttonLayout="split"
      />
    </div>
  );
};

SelectPrimaryLogin.propTypes = {
  navigate: PropTypes.func,
};

export default SelectPrimaryLogin;
